import { useEffect, useState } from "react";
import Modal from "./Modal";
import { apiGet } from "../../utils/api";
import { EhrProvider, PractitionerInfo } from "../../shared/types";
import { UserIcon } from "@heroicons/react/24/outline";

type PractitionerInfoModalProps = {
  orgId: number;
  ehrProvider: EhrProvider;
  practitionerId: string | null;
  practitionerName?: string;
  onClose: () => void;
};

const formatGender = (g: string) => {
  const lower = g?.toLowerCase();
  if (lower === "male") return "Male";
  if (lower === "female") return "Female";
  if (lower === "other" || lower === "unknown") return lower;
  return g;
};

const telecomLabel = (system?: string) => {
  const s = (system || "").toLowerCase();
  if (s === "email") return "Email";
  if (s === "phone" || s === "mobile" || s === "fax") return system === "fax" ? "Fax" : "Phone";
  return system || "Contact";
};

const PractitionerInfoModal = ({
  orgId,
  ehrProvider,
  practitionerId,
  practitionerName,
  onClose,
}: PractitionerInfoModalProps) => {
  const [info, setInfo] = useState<PractitionerInfo | null>(null);
  const [loading, setLoading] = useState(!!practitionerId);
  const [error, setError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState(false);

  useEffect(() => {
    if (!practitionerId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPhotoError(false);
    const url = `/api/${orgId}/ehr/practitioner/${practitionerId}`;
    apiGet(url)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || data.error) {
          if (data.error) setError(data.error);
          return;
        }
        setInfo({
          practitioner_id: data.practitioner_id,
          name: data.name ?? practitionerName ?? "Unknown",
          nameDetails: data.nameDetails,
          active: data.active,
          gender: data.gender,
          photo: data.photo,
          location: data.location,
          locationPhone: data.locationPhone,
          practitionerEmail: data.practitionerEmail,
          specialty: data.specialty,
          telecom: data.telecom,
          qualification: data.qualification,
        });
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load practitioner details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, ehrProvider, practitionerId, practitionerName]);

  const displayName = info?.name ?? practitionerName ?? "Unknown";

  return (
    <Modal title="Practitioner" onClose={onClose}>
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-rose-600">{error}</p>
      ) : !practitionerId ? (
        <p className="text-gray-600">
          {practitionerName || "—"}
          <span className="block mt-2 text-sm text-gray-500">No additional details available.</span>
        </p>
      ) : info ? (
        <div className="space-y-0">
          <div className="flex gap-4 items-start pb-4">
            <div className="h-20 w-20 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center ring-2 ring-gray-100">
              {info.photo && info.photo.length > 300 && !photoError ? (
                <img
                  src={info.photo}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setPhotoError(true)}
                />
              ) : (
                <UserIcon className="h-10 w-10 text-brand-600" />
              )}
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">{displayName}</h3>
              {info.nameDetails?.text && info.nameDetails.text !== displayName && (
                <p className="text-sm text-gray-500 mt-0.5">{info.nameDetails.text}</p>
              )}
              {(info.active !== undefined || info.gender) && (
                <p className="text-sm text-gray-500 mt-1.5">
                  {info.active !== undefined && (info.active ? "Active" : "Inactive")}
                  {info.active !== undefined && info.gender && " · "}
                  {info.gender && formatGender(info.gender)}
                </p>
              )}
            </div>
          </div>

          {(info.practitionerEmail || info.location || info.locationPhone || (info.specialty && info.specialty.length > 0) || (info.telecom && info.telecom.length > 0)) && (
            <div className="border-t border-gray-100 pt-4 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">Contact</p>
              <dl className="space-y-2">
                {info.practitionerEmail && (
                  <div>
                    <dt className="text-xs text-gray-500 font-medium">Email</dt>
                    <dd>
                      <a href={`mailto:${info.practitionerEmail}`} className="text-sm text-brand-600 hover:underline">
                        {info.practitionerEmail}
                      </a>
                    </dd>
                  </div>
                )}
                {info.location && (
                  <div>
                    <dt className="text-xs text-gray-500 font-medium">Location</dt>
                    <dd className="text-sm text-gray-700">{info.location}</dd>
                  </div>
                )}
                {info.locationPhone && (
                  <div>
                    <dt className="text-xs text-gray-500 font-medium">Phone</dt>
                    <dd>
                      <a href={`tel:${info.locationPhone}`} className="text-sm text-brand-600 hover:underline">
                        {info.locationPhone}
                      </a>
                    </dd>
                  </div>
                )}
                {info.specialty && info.specialty.length > 0 && (
                  <div>
                    <dt className="text-xs text-gray-500 font-medium">Specialty</dt>
                    <dd className="text-sm text-gray-700">{info.specialty.join(", ")}</dd>
                  </div>
                )}
                {info.telecom && info.telecom.length > 0 && info.telecom.map((t, i) => (
                  <div key={i}>
                    <dt className="text-xs text-gray-500 font-medium">{telecomLabel(t.system)}</dt>
                    <dd>
                      {t.system === "email" ? (
                        <a href={`mailto:${t.value}`} className="text-sm text-brand-600 hover:underline">{t.value}</a>
                      ) : (t.system === "phone" || t.system === "mobile" || t.system === "fax") ? (
                        <a href={`tel:${t.value}`} className="text-sm text-brand-600 hover:underline">{t.value}</a>
                      ) : (
                        <span className="text-sm text-gray-700">{t.value}</span>
                      )}
                      {t.use && <span className="text-gray-400 ml-1 text-sm">({t.use})</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {info.qualification && info.qualification.length > 0 && (
            <div className="border-t border-gray-100 pt-4 pb-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">Qualifications</p>
              <ul className="space-y-1.5">
                {info.qualification.map((q, i) => (
                  <li key={i} className="text-sm text-gray-600">
                    {q.text || q.code || "—"}
                    {q.period?.start && (
                      <span className="ml-1 text-gray-500">
                        ({q.period.start}
                        {q.period.end ? ` – ${q.period.end}` : ""})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-600">
          {displayName}
          <span className="block mt-2 text-sm text-gray-500">No additional details available.</span>
        </p>
      )}
    </Modal>
  );
};

export default PractitionerInfoModal;
